import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { Check } from 'lucide-react';
import { services, categories } from '../data/services';

const Services = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const location = useLocation();

  // Handle hash navigation
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  const filteredServices =
    activeCategory === 'All'
      ? services
      : services.filter((s) => s.category === activeCategory);

  return (
    <main data-testid="services-page" className="pt-24 md:pt-32">
      {/* Hero */}
      <section className="container-wide pb-16 md:pb-24">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="eyebrow mb-6 block"
        >
          Services
        </motion.span>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-huge font-['DM_Sans'] max-w-4xl"
        >
          Full-service signage and printing solutions
        </motion.h1>
      </section>

      {/* Filter Tabs */}
      <section className="border-y border-border sticky top-20 md:top-24 bg-background z-30">
        <div className="container-wide">
          <div className="flex items-center gap-2 md:gap-4 py-4 overflow-x-auto scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                data-testid={`filter-${category.toLowerCase().replace(' ', '-')}`}
                onClick={() => setActiveCategory(category)}
                className={`px-4 md:px-6 py-2 md:py-3 text-xs md:text-sm uppercase tracking-wider font-medium whitespace-nowrap rounded-full transition-colors ${
                  activeCategory === category
                    ? 'bg-foreground text-background'
                    : 'bg-secondary hover:bg-border'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="container-wide py-16 md:py-24">
        <div className="space-y-16 md:space-y-24">
          {filteredServices.map((service, index) => (
            <motion.div
              key={service.id}
              id={service.id}
              data-testid={`service-detail-${service.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8 }}
              className="scroll-mt-40"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
                {/* Number & Title */}
                <div className="lg:col-span-5">
                  <span className="eyebrow mb-4 block">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight font-['DM_Sans'] mb-4">
                    {service.title}
                  </h2>
                  <span className="pill-tag">{service.category}</span>
                </div>

                {/* Description & Features */}
                <div className="lg:col-span-7">
                  <p className="text-lg md:text-xl text-muted-foreground mb-8">
                    {service.description}
                  </p>

                  {/* Features bullets */}
                  {service.features && service.features.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="eyebrow mb-4">Features</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {service.features.map((feature, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Check className="w-3 h-3 text-primary" />
                            </div>
                            <span className="text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Products list (for Other Signage) */}
                  {service.products && service.products.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="eyebrow mb-4">Products Available</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {service.products.map((product, i) => (
                          <span
                            key={i}
                            className="text-xs py-2 px-3 bg-secondary rounded text-center"
                          >
                            {product}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Divider */}
              {index < filteredServices.length - 1 && (
                <div className="section-divider mt-16 md:mt-24" />
              )}
            </motion.div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Services;
